/**
 * Surfacing for the API's `warnings` channel.
 *
 * A warning means the request SUCCEEDED and something about it needs saying —
 * the contract is written out on `ApiResponse.warnings` in
 * backend/schemas/auth.py, which also forbids using it to report a failure. It
 * had no way of reaching a screen: `apiPost` unwraps to `response.data.data`
 * and discards the envelope, so every warning any endpoint returned was thrown
 * away by the helper that fetched it. The one page that wanted to warn wrote
 * its own copy of the sentence instead.
 *
 * 🔴 A WARNING MUST NOT READ AS AN ERROR. If people cannot tell the two apart
 * they learn to dismiss both, and then the errors stop working too. Four
 * separations, because any one alone is too easy to miss:
 *
 *   - it NEVER replaces the success toast. Both appear, success first, so the
 *     reading order is "it worked" and then "and note this". A lone toast
 *     after a submit is exactly what reads as failure;
 *   - amber, with a triangle — not the red circle of an error or the green
 *     tick of a success;
 *   - it says what happened or what will happen, never that the caller must
 *     act. `test_warnings_never_read_as_failures` enforces the wording;
 *   - it lasts longer and is dismissible, because unlike a success it carries
 *     something the reader may need to act on later.
 */
import toast from 'react-hot-toast';

/** Long enough to read a sentence and decide, rather than a glance. */
const WARNING_DURATION_MS = 12_000;

/**
 * Warnings raised while the page was being replaced.
 *
 * A toast lives in the <Toaster/> mounted beside <App/> in main.tsx, OUTSIDE
 * the route tree, so ordinary SPA navigation cannot unmount it. A full page
 * load can, and there are real ones: the two `window.location.href = '/login'`
 * redirects in utils/api.ts, SettingsPage's logout, shellRecovery's reload. A
 * warning raised just before one of those would never be seen, and a warning
 * that is never seen was not shown.
 *
 * So it is parked here and drained once on the next mount. sessionStorage, not
 * local: it belongs to this tab and this session, and a warning about
 * something that happened yesterday is noise.
 */
const QUEUE_KEY = 'spaciora.pending-warnings';

interface Parked { message: string; at: number }

function readQueue(): Parked[] {
  try {
    const raw = sessionStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is Parked =>
        !!p && typeof p === 'object' &&
        typeof (p as Parked).message === 'string' &&
        typeof (p as Parked).at === 'number',
    );
  } catch {
    // A private window, cleared site data, or a browser refusing storage. A
    // warning is worth less than a crash on the way to showing it.
    return [];
  }
}

function writeQueue(items: Parked[]): void {
  try {
    sessionStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  } catch {
    /* see readQueue */
  }
}

/**
 * Park a warning that has just been shown, in case a load is about to cut it
 * short. Capped, so a burst cannot fill the tab's storage.
 */
export function queueWarning(message: string): void {
  writeQueue([...readQueue(), { message, at: Date.now() }].slice(-10));
}

/**
 * Re-show anything a page load interrupted, then forget it.
 *
 * The age test is what keeps this from replaying old news. A warning younger
 * than the toast's own lifetime means the app mounted while that toast should
 * still have been on screen — which only happens when a load destroyed it, so
 * it is shown again. Anything older had its full run and is dropped.
 *
 * The queue is cleared either way, so nothing can survive into a second load
 * and surface on a screen it has nothing to do with.
 */
export function drainQueuedWarnings(now: number = Date.now()): void {
  const parked = readQueue();
  if (!parked.length) return;
  writeQueue([]);
  parked
    .filter((p) => now - p.at < WARNING_DURATION_MS)
    .forEach((p) => showWarning(p.message));
}

/**
 * One warning, on screen.
 *
 * The id is derived from the message so a double submit, a retry or two
 * components reacting to one response collapse into a single toast instead of
 * stacking three identical ones.
 */
export function showWarning(message: string): void {
  toast(message, {
    id: `warning:${message}`,
    duration: WARNING_DURATION_MS,
    icon: '⚠️',
    ariaProps: { role: 'status', 'aria-live': 'polite' },
    className: 'spaciora-warning-toast',
    style: {
      background: 'rgb(var(--color-warning-surface-50, 254 252 232))',
      color: 'rgb(var(--color-warning-content-800, 133 77 14))',
      border: '1px solid rgb(var(--color-warning-surface-500, 234 179 8))',
      maxWidth: '30rem',
      lineHeight: 1.5,
    },
  });
}

/**
 * Pull the warnings out of a response envelope.
 *
 * Defensive about the shape because this runs on EVERY response, including
 * ones that are not our envelope at all — a file download, a proxy's error
 * page. Anything unexpected yields nothing rather than throwing inside an
 * interceptor, where a throw would turn a successful request into a failed one.
 */
export function warningsFrom(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') return [];
  const raw = (payload as { warnings?: unknown }).warnings;
  if (!Array.isArray(raw)) return [];
  return raw.filter((w): w is string => typeof w === 'string' && w.trim().length > 0);
}
